import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "");
const BUBBLE = { contractor: "bg-slate-100 text-slate-900", ava: "bg-[#0B192C] text-white ml-auto", agent: "bg-amber-100 text-slate-900 ml-auto" };

const Thread = ({ id, onSent }) => {
  const [t, setT] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/surety/texts/${id}`).then((r) => setT(r.data)).catch((e) => toast.error(apiError(e))); }, [id]);
  if (!t) return null;
  const send = async () => {
    setBusy(true);
    try { setT((await api.post(`/surety/texts/${id}`, { text })).data); setText(""); onSent(); } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <Card className="p-5" data-testid="surety-text-thread">
      <div className="mb-3 font-semibold text-slate-900">{t.lead.name}{t.lead.business ? ` · ${t.lead.business}` : ""} <span className="font-normal text-slate-500">{t.lead.phone}</span></div>
      <div className="mb-4 max-h-[55vh] space-y-2 overflow-y-auto">
        {t.messages.map((m, i) => (
          <div key={i} className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${BUBBLE[m.role] || BUBBLE.contractor}`} data-testid={`surety-text-msg-${i}`}>
            <div>{m.text}</div><div className="mt-1 text-[11px] opacity-70">{m.name} · {fmt(m.at)}</div>
          </div>
        ))}
      </div>
      {t.lead.opt_out ? <p className="text-sm text-red-700">This contractor opted out or is on a Do-Not-Call list.</p>
        : !t.enabled ? <p className="text-sm text-slate-500">Texting is switched off until business texting approval comes through.</p>
        : (<div className="flex flex-col gap-2 sm:flex-row">
            <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a text to this contractor" data-testid="surety-text-input" />
            <Button onClick={send} disabled={busy || !text.trim()} data-testid="surety-text-send">Send</Button>
          </div>)}
    </Card>
  );
};

export default function SuretyTexts() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => { api.get("/surety/texts").then((r) => setRows(r.data.threads)).catch((e) => toast.error(apiError(e))); };
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="surety-texts-page">
      <PageHeader title="Texts" subtitle="Every text Ava and you send to contractors, and their replies. Ava steps back once you text." />
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[340px_1fr]">
        <Card className="p-2" data-testid="surety-text-list">
          {rows && !rows.length && <p className="p-4 text-sm text-slate-500">No texts yet. Autopilot and QR scans text contractors automatically.</p>}
          {rows?.map((r) => (
            <button key={r.lead_id} type="button" onClick={() => setOpen(r.lead_id)} data-testid={`surety-text-row-${r.lead_id}`}
              className={`block w-full rounded-lg px-3 py-2 text-left transition-colors hover:bg-slate-50 ${open === r.lead_id ? "bg-slate-100" : ""}`}>
              <div className="flex justify-between gap-2 text-sm font-semibold text-slate-900"><span>{r.lead_name}{r.unread && <span className="ml-2 inline-block h-2 w-2 rounded-full bg-amber-500" />}</span><span className="text-xs font-normal text-slate-400">{fmt(r.at)}</span></div>
              <div className="truncate text-xs text-slate-500">{r.last_text}</div>
            </button>
          ))}
        </Card>
        {open ? <Thread key={open} id={open} onSent={load} /> : <Card className="hidden p-8 text-sm text-slate-500 lg:block">Pick a conversation.</Card>}
      </div>
    </div>
  );
}
